import React, { useEffect, useMemo, useState } from 'react';
import { Calendar, ArrowUpDown, ArrowUp, ArrowDown, FileSpreadsheet, BarChart3, X, ChevronsLeft, ChevronsRight, ChevronLeft, ChevronRight, Search, Eraser, Filter, ShoppingCart, Banknote } from 'lucide-react';
import * as XLSX from 'xlsx';
import { useAuth } from '../contexts/AuthContext';
import { relatorioService } from '../services/relatorioService';
import toast from 'react-hot-toast';
import IconButton from '../components/IconButton';

const COLUMNS = [
  { key: 'CODIGO_CLIENTE', label: 'Cliente' },
  { key: 'NOME_CLIENTE', label: 'Nome Cliente' },
  { key: 'CONTRATO', label: 'Contrato' },
  { key: 'OS', label: 'OS' },
  { key: 'CODIGO_VENDA', label: 'Venda' },
  { key: 'CODIGO_VENDEDOR', label: 'Vendedor' },
  { key: 'NOME_VENDEDOR', label: 'Nome Vendedor' },
  { key: 'VALOR_VENDA', label: 'Valor do Produto' },
  { key: 'VALOR_SERVICOS', label: 'Valor de Serviços' },
  { key: 'VALOR_NOTA', label: 'Valor Total' },
  { key: 'SERVICO_OU_VENDA', label: 'Tipo' },
  { key: 'TB02021_DATA', label: 'Data' },
];

// Colunas para a aba Vendas Concluídas (vw_VendasBaixadas)
const COLUMNS_CONCLUIDAS = [
  { key: 'CODIGO_VENDA', label: 'Venda' },
  { key: 'OS', label: 'OS' },
  { key: 'SERVICO_OU_VENDA', label: 'Tipo' },
  { key: 'DATA_VENDA', label: 'Data da Venda' },
  { key: 'DATA_BAIXA', label: 'Data da Baixa' },
  { key: 'NOME_VENDEDOR', label: 'Nome Vendedor' },
  { key: 'VALOR_NOTA', label: 'Valor da Nota' },
  { key: 'VALOR_VENDA', label: 'Valor da Venda' },
  { key: 'VALOR_PAGO', label: 'Valor Pago' },
];

const numericKeys = ['VALOR_VENDA', 'VALOR_SERVICOS', 'VALOR_NOTA'];
const CURRENCY_FMT = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const formatCurrencyBRL = (value) => {
  const n = typeof value === 'number' ? value : Number(value) || 0;
  return CURRENCY_FMT.format(n);
};
const dateKeys = ['TB02021_DATA'];
const numericKeysConcluidas = ['VALOR_VENDA', 'VALOR_NOTA', 'VALOR_PAGO'];
const dateKeysConcluidas = ['DATA_VENDA', 'DATA_BAIXA'];
const formatDateBR = (val) => {
  if (!val) return '';
  if (typeof val === 'string') {
    const s = val.trim();
    // YYYY-MM-DD or ISO
    const m = s.match(/^([0-9]{4})-([0-9]{2})-([0-9]{2})/);
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
    // YYYYMMDD
    const c = s.match(/^([0-9]{4})([0-9]{2})([0-9]{2})$/);
    if (c) return `${c[3]}/${c[2]}/${c[1]}`;
  }
  try {
    const d = new Date(val);
    if (!isNaN(d)) {
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const yyyy = d.getFullYear();
      return `${dd}/${mm}/${yyyy}`;
    }
  } catch {}
  return String(val ?? '');
};
const aggregationOptions = [
  { key: 'sum', label: 'Soma' },
  { key: 'avg', label: 'Média' },
  { key: 'max', label: 'Máximo' },
  { key: 'min', label: 'Mínimo' },
  { key: 'count', label: 'Contagem' },
];

function aggregate(rows, aggType, keys = numericKeys) {
  const acc = {};
  keys.forEach(k => acc[k] = 0);

  const getVal = (r, k) => {
    if (k === 'VALOR_SERVICOS') {
      const total = Number(r['VALOR_NOTA']) || 0;
      const produto = Number(r['VALOR_VENDA']) || 0;
      return total - produto;
    }
    return Number(r[k]) || 0;
  };

  if (aggType === 'count') return { COUNT: rows.length };

  if (aggType === 'sum' || aggType === 'avg') {
    rows.forEach(r => keys.forEach(k => acc[k] += getVal(r, k)));
    if (aggType === 'avg') keys.forEach(k => acc[k] = rows.length ? acc[k] / rows.length : 0);
    return acc;
  }
  if (aggType === 'max') {
    keys.forEach(k => acc[k] = rows.reduce((m, r) => Math.max(m, getVal(r, k)), 0));
    return acc;
  }
  if (aggType === 'min') {
    keys.forEach(k => acc[k] = rows.reduce((m, r) => Math.min(m, getVal(r, k)), Number.POSITIVE_INFINITY));
    return acc;
  }
  return acc;
}

const Relatorio = () => {
  const { user } = useAuth();
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const itemsPerPageOptions = [10, 25, 50, 100];
  const [pageSize, setPageSize] = useState(() => {
    try {
      const saved = localStorage.getItem('relatorio_itemsPerPage');
      const num = saved ? Number(saved) : 50;
      return itemsPerPageOptions.includes(num) ? num : 50;
    } catch {
      return 50;
    }
  });
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  // Filtros específicos para aba Concluídas
  const [baixaStartDate, setBaixaStartDate] = useState('');
  const [baixaEndDate, setBaixaEndDate] = useState('');
  const [vendaStartDate, setVendaStartDate] = useState('');
  const [vendaEndDate, setVendaEndDate] = useState('');
  const [showDateFilters, setShowDateFilters] = useState(false);
  const [sortBy, setSortBy] = useState('TB02021_DATA');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [groupBy, setGroupBy] = useState([]); // ex.: ['NOME_CLIENTE', 'NOME_VENDEDOR']
  const [aggType, setAggType] = useState('sum');
  const [activeTab, setActiveTab] = useState('vendas');
  const [smartQuery, setSmartQuery] = useState('');

  // Configurações dinâmicas por aba
  const columns = useMemo(() => activeTab === 'vendas' ? COLUMNS : COLUMNS_CONCLUIDAS, [activeTab]);
  const numericKeysActive = useMemo(() => activeTab === 'vendas' ? numericKeys : numericKeysConcluidas, [activeTab]);
  const dateKeysActive = useMemo(() => activeTab === 'vendas' ? dateKeys : dateKeysConcluidas, [activeTab]);

  const availableGroupColumns = useMemo(() => columns.filter(c => !groupBy.includes(c.key)), [columns, groupBy]);

  // Fallback seguro para leitura de valores por chave (especialmente datas)
  const getRowValue = (row, key) => {
    if (!row) return undefined;
    // Coluna calculada: Valor de Serviços = Valor Total - Valor do Produto
    if (key === 'VALOR_SERVICOS') {
      const total = Number(row['VALOR_NOTA']) || 0;
      const produto = Number(row['VALOR_VENDA']) || 0;
      return total - produto;
    }
    if (row[key] !== undefined && row[key] !== null) return row[key];
    // Mapeamento de chaves alternativas quando origem vier com nomes diferentes
    const altMap = {
      DATA_VENDA: ['DATA_VENDA', 'DT_VENDA', 'DATA VENDA'],
      DATA_BAIXA: ['DATA_BAIXA', 'DT_BAIXA', 'DATA BAIXA']
    };
    const alts = altMap[key];
    if (alts) {
      for (const k of alts) {
        if (row[k] !== undefined && row[k] !== null) return row[k];
      }
    }
    return undefined;
  };

  const fetchData = async (overrideQ) => {
    setLoading(true);
    try {
      const resp = activeTab === 'vendas'
        ? await relatorioService.listarVendas({ startDate, endDate, sortBy, sortOrder, page, pageSize, q: (overrideQ !== undefined ? overrideQ : smartQuery) })
        : await relatorioService.listarVendasConcluidas({
            // alias antigo continua funcionando, mas priorizamos específicos
            baixaStartDate,
            baixaEndDate,
            vendaStartDate,
            vendaEndDate,
            sortBy,
            sortOrder,
            page,
            pageSize,
            q: (overrideQ !== undefined ? overrideQ : smartQuery)
          });
      if (!resp.success) throw new Error('Falha ao carregar dados');
      setData(resp.data || []);
      setTotal(resp.total || 0);
    } catch (err) {
      console.error(err);
      toast.error('Erro ao carregar relatório');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, sortBy, sortOrder, activeTab, baixaStartDate, baixaEndDate, vendaStartDate, vendaEndDate]);

  // Persistir tamanho da página e ajustar página ao mudar total
  useEffect(() => {
    try { localStorage.setItem('relatorio_itemsPerPage', String(pageSize)); } catch {}
  }, [pageSize]);

  useEffect(() => {
    const totalPagesCalc = Math.max(1, Math.ceil((total || 0) / pageSize));
    if (page > totalPagesCalc) setPage(totalPagesCalc);
  }, [total, pageSize, page]);

  const toggleSort = (key) => {
    if (sortBy === key) {
      setSortOrder(prev => prev === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(key);
      setSortOrder('ASC');
    }
  };

  // Drag & Drop (nativo): arrastar chips de colunas para área de quebras
  const onDragStart = (e, key) => {
    e.dataTransfer.setData('text/plain', key);
  };
  const onDropGroup = (e) => {
    const key = e.dataTransfer.getData('text/plain');
    if (key && !groupBy.includes(key)) setGroupBy(prev => [...prev, key]);
  };
  const onDragOver = (e) => e.preventDefault();
  const removeGroupKey = (key) => setGroupBy(prev => prev.filter(k => k !== key));

  // Agrupamento hierárquico
  function groupData(rows, keys) {
    if (!keys.length) return [{ key: null, value: null, rows }];
    const [first, ...rest] = keys;
    const map = new Map();
    rows.forEach(r => {
      const val = r[first];
      const bucket = map.get(val) || [];
      bucket.push(r);
      map.set(val, bucket);
    });
    return Array.from(map.entries()).map(([val, bucket]) => ({
      key: first,
      value: val,
      rows: bucket,
      children: groupData(bucket, rest)
    }));
  }

  const grouped = useMemo(() => groupBy.length ? groupData(data, groupBy) : null, [data, groupBy]);
  const grandTotal = useMemo(() => aggregate(data, 'sum', numericKeysActive), [data, numericKeysActive]);

  const exportToExcel = async () => {
    try {
      toast.loading('Preparando exportação com filtros e quebras…', { id: 'export-report' });

      // Buscar TODOS os dados filtrados, ignorando paginação atual
      const resp = activeTab === 'vendas'
        ? await relatorioService.listarVendas({
            startDate,
            endDate,
            sortBy,
            sortOrder,
            page: 1,
            pageSize: Math.max(total || 0, 100000),
            q: smartQuery
          })
        : await relatorioService.listarVendasConcluidas({
            baixaStartDate,
            baixaEndDate,
            vendaStartDate,
            vendaEndDate,
            sortBy,
            sortOrder,
            page: 1,
            pageSize: Math.max(total || 0, 100000),
            q: smartQuery
          });
      if (!resp.success) throw new Error('Falha ao carregar dados para exportação');
      const allRows = resp.data || [];

      // Reaplicar agrupamentos sobre todos os dados
      const allGrouped = groupBy.length ? groupData(allRows, groupBy) : null;
      const allGrandTotal = aggregate(allRows, 'sum', numericKeysActive);

      // Montar planilha principal (com filtros e quebras)
      const wb = XLSX.utils.book_new();
      const aoa = [];

      // Cabeçalho com período
      aoa.push([activeTab === 'vendas' ? 'Relatório - Vendas Realizadas' : 'Relatório - Vendas Recebidas']);
      if (activeTab === 'vendas') {
        const periodoLabel = (startDate || endDate)
          ? `Período: ${startDate ? formatDateBR(startDate) : '-'} a ${endDate ? formatDateBR(endDate) : '-'}`
          : 'Período: todos';
        aoa.push([periodoLabel]);
      } else {
        const baixaLabel = (baixaStartDate || baixaEndDate)
          ? `Baixa: ${baixaStartDate ? formatDateBR(baixaStartDate) : '-'} a ${baixaEndDate ? formatDateBR(baixaEndDate) : '-'}`
          : null;
        const vendaLabel = (vendaStartDate || vendaEndDate)
          ? `Venda: ${vendaStartDate ? formatDateBR(vendaStartDate) : '-'} a ${vendaEndDate ? formatDateBR(vendaEndDate) : '-'}`
          : null;
        if (baixaLabel) aoa.push([baixaLabel]);
        if (vendaLabel) aoa.push([vendaLabel]);
        if (!baixaLabel && !vendaLabel) aoa.push(['Período: todos']);
      }
      aoa.push([]);

      const header = columns.map(c => c.label);

      const pushTableRows = (rows) => {
        aoa.push([...header]);
        rows.forEach(r => {
          aoa.push(columns.map(c => {
            const val = getRowValue(r, c.key);
            if (numericKeysActive.includes(c.key)) return formatCurrencyBRL(Number(val) || 0);
            if (dateKeysActive.includes(c.key)) return formatDateBR(val);
            return String(val ?? '');
          }));
        });
      };

      const pushSubtotalRow = (rows) => {
        const ag = aggregate(rows, aggType, numericKeysActive);
        const subtotalRow = new Array(columns.length).fill('');
        const labelCell = `Subtotal (${aggregationOptions.find(a => a.key === aggType)?.label || aggType})`;
        subtotalRow[0] = labelCell;
        const idxVenda = columns.findIndex(c => c.key === 'VALOR_VENDA');
        const idxServicos = columns.findIndex(c => c.key === 'VALOR_SERVICOS');
        const idxNota = columns.findIndex(c => c.key === 'VALOR_NOTA');
        const idxPago = columns.findIndex(c => c.key === 'VALOR_PAGO');
        if (idxVenda >= 0) subtotalRow[idxVenda] = formatCurrencyBRL(Number(ag['VALOR_VENDA'] || 0));
        if (idxServicos >= 0) subtotalRow[idxServicos] = formatCurrencyBRL(Number(ag['VALOR_SERVICOS'] || 0));
        if (idxNota >= 0) subtotalRow[idxNota] = formatCurrencyBRL(Number(ag['VALOR_NOTA'] || 0));
        if (idxPago >= 0) subtotalRow[idxPago] = formatCurrencyBRL(Number(ag['VALOR_PAGO'] || 0));
        aoa.push(subtotalRow);
      };

      const pushGrouped = (groups, level = 0) => {
        groups.forEach(g => {
          const indent = '  '.repeat(level);
          const groupColLabel = columns.find(c => c.key === g.key)?.label || g.key;
          aoa.push([`${indent}${groupColLabel}: ${String(g.value ?? '')}`]);
          aoa.push([]);
          if (g.children && g.children.length) {
            pushGrouped(g.children, level + 1);
          } else {
            pushTableRows(g.rows);
            pushSubtotalRow(g.rows);
          }
          aoa.push([]);
        });
      };

      if (allGrouped) {
        pushGrouped(allGrouped);
      } else {
        pushTableRows(allRows);
      }

      // Total geral ao final
      aoa.push([]);
      const totalRow = new Array(columns.length).fill('');
      totalRow[0] = 'Total Geral';
      const idxVenda = columns.findIndex(c => c.key === 'VALOR_VENDA');
      const idxServicos = columns.findIndex(c => c.key === 'VALOR_SERVICOS');
      const idxNota = columns.findIndex(c => c.key === 'VALOR_NOTA');
      const idxPago = columns.findIndex(c => c.key === 'VALOR_PAGO');
      if (idxVenda >= 0) totalRow[idxVenda] = formatCurrencyBRL(Number(allGrandTotal['VALOR_VENDA'] || 0));
      if (idxServicos >= 0) totalRow[idxServicos] = formatCurrencyBRL(Number(allGrandTotal['VALOR_SERVICOS'] || 0));
      if (idxNota >= 0) totalRow[idxNota] = formatCurrencyBRL(Number(allGrandTotal['VALOR_NOTA'] || 0));
      if (idxPago >= 0) totalRow[idxPago] = formatCurrencyBRL(Number(allGrandTotal['VALOR_PAGO'] || 0));
      aoa.push(totalRow);

      const ws = XLSX.utils.aoa_to_sheet(aoa);
      XLSX.utils.book_append_sheet(wb, ws, activeTab === 'vendas' ? 'Vendas Realizadas' : 'Vendas Recebidas');

      // Gravar arquivo
      const filename = `${activeTab === 'vendas' ? 'Relatorio_Vendas' : 'Relatorio_Vendas_Recebidas'}_${new Date().toISOString().slice(0,10)}.xlsx`;
      XLSX.writeFile(wb, filename);
      toast.success('Excel exportado com filtros e quebras aplicados', { id: 'export-report' });
    } catch (err) {
      console.error(err);
      toast.error('Falha ao exportar Excel');
      toast.dismiss('export-report');
    }
  };

  const totalPages = Math.max(1, Math.ceil((total || 0) / pageSize));
  const startIndexUI = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const endIndexUI = Math.min(page * pageSize, total || 0);
  const pageNumbers = useMemo(() => {
    const pages = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }
    const left = Math.max(1, page - 1);
    const right = Math.min(totalPages, page + 1);
    pages.push(1);
    if (left > 2) pages.push('...');
    for (let i = left; i <= right; i++) {
      if (i !== 1 && i !== totalPages) pages.push(i);
    }
    if (right < totalPages - 1) pages.push('...');
    if (totalPages > 1) pages.push(totalPages);
    return pages;
  }, [page, totalPages]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <BarChart3 className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Relatório</h1>
            <p className="text-gray-600">Vendas por período com agrupamentos e totais</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <IconButton
            icon={FileSpreadsheet}
            onClick={exportToExcel}
            variant="success"
            title="Exportar Excel"
          />
        </div>
      </div>

      {/* Abas de Relatórios */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-2">
        <div className="flex items-center gap-2">
          <IconButton
            icon={ShoppingCart}
            onClick={() => setActiveTab('vendas')}
            variant={activeTab === 'vendas' ? 'primary' : 'secondary'}
            title="Vendas Realizadas"
          />
          <IconButton
            icon={Banknote}
            onClick={() => { setActiveTab('concluidas'); setSortBy('DATA_BAIXA'); setGroupBy([]); setShowDateFilters(false); }}
            variant={activeTab === 'concluidas' ? 'primary' : 'secondary'}
            title="Vendas Recebidas"
          />
        </div>
      </div>

      {(activeTab === 'vendas' || activeTab === 'concluidas') && (
        <>
      {/* Filtros */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          {activeTab === 'vendas' ? (
            <>
              <div>
                <label className="text-sm text-gray-700">Início</label>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-500" />
                  <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="border rounded px-2 py-1 w-full" />
                </div>
              </div>
              <div>
                <label className="text-sm text-gray-700">Fim</label>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-500" />
                  <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="border rounded px-2 py-1 w-full" />
                </div>
              </div>
            </>
          ) : (
            <div className="relative">
              <label className="text-sm text-gray-700">Filtros de Data</label>
              <IconButton
                icon={Calendar}
                onClick={() => setShowDateFilters(v => !v)}
                variant="primary"
                title="Configurar filtros de data"
              />
              {showDateFilters && (
                <div className="absolute right-0 mt-2 w-96 bg-white border border-gray-200 rounded-lg shadow-lg z-10 p-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-800">Datas</span>
                    <IconButton
                      icon={X}
                      onClick={() => setShowDateFilters(false)}
                      variant="secondary"
                      size="sm"
                      title="Fechar"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-gray-600">Baixa — Início</label>
                      <input type="date" value={baixaStartDate} onChange={e => setBaixaStartDate(e.target.value)} className="border rounded px-2 py-1 w-full" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-600">Baixa — Fim</label>
                      <input type="date" value={baixaEndDate} onChange={e => setBaixaEndDate(e.target.value)} className="border rounded px-2 py-1 w-full" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-600">Venda — Início</label>
                      <input type="date" value={vendaStartDate} onChange={e => setVendaStartDate(e.target.value)} className="border rounded px-2 py-1 w-full" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-600">Venda — Fim</label>
                      <input type="date" value={vendaEndDate} onChange={e => setVendaEndDate(e.target.value)} className="border rounded px-2 py-1 w-full" />
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-2">
                    <IconButton
                      icon={Eraser}
                      onClick={() => { setBaixaStartDate(''); setBaixaEndDate(''); setVendaStartDate(''); setVendaEndDate(''); setPage(1); }}
                      variant="secondary"
                      size="sm"
                      title="Limpar"
                    />
                    <IconButton
                      icon={Filter}
                      onClick={() => { setShowDateFilters(false); setPage(1); }}
                      variant="primary"
                      size="sm"
                      title="Aplicar"
                    />
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="md:col-span-4">
            <label className="text-sm text-gray-700">Filtro inteligente</label>
            <div className="flex items-center gap-2">
              <Search className="h-4 w-4 text-gray-500" />
              <input
                type="text"
                value={smartQuery}
                onChange={e => setSmartQuery(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const currentValue = e.currentTarget.value.trim();
                    setSmartQuery(currentValue);
                    setPage(1);
                    fetchData(currentValue);
                  }
                }}
                placeholder="Ex.: N:regina OS:V001 VP>200 DV:2025-09-01..2025-09-30"
                className="border rounded px-2 py-1 w-full"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Aliases: N (Vendedor), NC (Cliente), V (Venda), OS, C (Cliente ID), T (Tipo), P (Valor Produto), VT (Valor Nota), VP (Valor Pago), D (Data Nota), DV (Data Venda), DB (Data Baixa). Operadores: :, &gt;, &lt;, &gt;=, &lt;=, intervalo ..
            </p>
          </div>
          <div>
            <label className="text-sm text-gray-700">Agregação</label>
            <select value={aggType} onChange={e => setAggType(e.target.value)} className="border rounded px-2 py-1 w-full">
              {aggregationOptions.map(opt => (
                <option key={opt.key} value={opt.key}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div className="flex gap-2">
            <IconButton
              icon={Filter}
              onClick={() => { setPage(1); fetchData(); }}
              variant="primary"
              title="Aplicar filtros"
            />
            <IconButton
              icon={Eraser}
              onClick={() => { setStartDate(''); setEndDate(''); setBaixaStartDate(''); setBaixaEndDate(''); setVendaStartDate(''); setVendaEndDate(''); setSmartQuery(''); setPage(1); fetchData(); }}
              variant="secondary"
              title="Limpar"
            />
          </div>
        </div>
      </div>

      {/* Área de Quebras (drag & drop) */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-900">Quebras (Agrupamento)</h2>
          <p className="text-xs text-gray-500">Arraste colunas para agrupar. Suporta múltiplas quebras.</p>
        </div>
        <div className="flex flex-wrap gap-2 mb-3">
          {availableGroupColumns.map(col => (
            <span key={col.key} draggable onDragStart={e => onDragStart(e, col.key)} className="px-3 py-1 border rounded-full text-sm bg-gray-100 hover:bg-gray-200 cursor-grab">
              {col.label}
            </span>
          ))}
        </div>
        <div onDrop={onDropGroup} onDragOver={onDragOver} className="min-h-[48px] border-2 border-dashed border-blue-300 rounded-md p-2 flex flex-wrap gap-2">
          {groupBy.length === 0 && (
            <span className="text-xs text-gray-500">Solte aqui para criar agrupamentos</span>
          )}
          {groupBy.map(key => {
            const col = columns.find(c => c.key === key);
            return (
              <span key={key} className="px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-sm flex items-center gap-2">
                {col?.label}
                <IconButton
                  icon={X}
                  onClick={() => removeGroupKey(key)}
                  variant="secondary"
                  size="sm"
                  className="!w-5 !h-5 !rounded-full"
                  title={`Remover agrupamento por ${col?.label}`}
                />
              </span>
            );
          })}
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="overflow-x-auto">
          {!grouped && (
            <table className="min-w-full">
              <thead>
                <tr>
                  {columns.map(col => (
                    <th key={col.key} className="px-2 py-2 text-left text-xs font-semibold text-gray-700 border-b">
                      <button className="inline-flex items-center gap-1" onClick={() => toggleSort(col.key)}>
                        {col.label}
                        {sortBy === col.key ? (sortOrder === 'ASC' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3" />}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td className="px-2 py-3 text-center text-sm" colSpan={columns.length}>Carregando...</td></tr>
                ) : data.length === 0 ? (
                  <tr><td className="px-2 py-3 text-center text-sm" colSpan={columns.length}>Nenhum dado</td></tr>
                ) : (
                  data.map((row, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      {columns.map(col => (
                        <td key={col.key} className="px-2 py-2 text-xs border-b">
                          {numericKeysActive.includes(col.key)
                            ? formatCurrencyBRL(getRowValue(row, col.key))
                            : dateKeysActive.includes(col.key)
                              ? formatDateBR(getRowValue(row, col.key))
                              : String(getRowValue(row, col.key) ?? '')}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}

          {grouped && (
            <div className="space-y-3">
              {grouped.map((g1, idx1) => (
                <div key={idx1} className="border rounded">
                  <div className="bg-gray-100 px-3 py-2 text-sm font-semibold">
                    {g1.key}: {g1.value}
                  </div>
                  <div className="p-2">
                    {/* Se houver níveis adicionais, renderiza recursivamente */}
                    {g1.children && g1.children.length > 0 ? (
                      g1.children.map((g2, idx2) => (
                        <div key={idx2} className="border rounded mb-2">
                          <div className="bg-gray-50 px-3 py-2 text-sm">
                            {g2.key}: {g2.value}
                          </div>
                          <table className="min-w-full">
                            <thead>
                              <tr>
                                {columns.map(col => (
                                  <th key={col.key} className="px-2 py-2 text-left text-xs font-semibold text-gray-700 border-b">{col.label}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {g2.rows.map((row, rIdx) => (
                                <tr key={rIdx} className="hover:bg-gray-50">
                                  {columns.map(col => (
                                    <td key={col.key} className="px-2 py-2 text-xs border-b">
                                      {numericKeysActive.includes(col.key)
                                        ? formatCurrencyBRL(getRowValue(row, col.key))
                                        : dateKeysActive.includes(col.key)
                                          ? formatDateBR(getRowValue(row, col.key))
                                          : String(getRowValue(row, col.key) ?? '')}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                              {/* Subtotal */}
                              <tr>
                                <td className="px-2 py-2 text-xs font-semibold" colSpan={columns.length}>
                                  Subtotal ({aggregationOptions.find(a => a.key === aggType)?.label}):
                                  {numericKeysActive.map(k => {
                                    const ag = aggregate(g2.rows, aggType, numericKeysActive);
                                    const val = ag[k] ?? ag.COUNT;
                                    const label = columns.find(c => c.key === k)?.label || k;
                                    return (
                                      <span key={k} className="ml-3">{formatCurrencyBRL(val)} ({label})</span>
                                    );
                                  })}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      ))
                    ) : (
                      <table className="min-w-full">
                        <thead>
                          <tr>
                            {columns.map(col => (
                              <th key={col.key} className="px-2 py-2 text-left text-xs font-semibold text-gray-700 border-b">{col.label}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {g1.rows.map((row, rIdx) => (
                            <tr key={rIdx} className="hover:bg-gray-50">
                              {columns.map(col => (
                                <td key={col.key} className="px-2 py-2 text-xs border-b">
                                  {numericKeysActive.includes(col.key)
                                    ? formatCurrencyBRL(getRowValue(row, col.key))
                                    : dateKeysActive.includes(col.key)
                                      ? formatDateBR(getRowValue(row, col.key))
                                      : String(getRowValue(row, col.key) ?? '')}
                                </td>
                              ))}
                            </tr>
                          ))}
                          {/* Subtotal */}
                          <tr>
                            <td className="px-2 py-2 text-xs font-semibold" colSpan={columns.length}>
                              Subtotal ({aggregationOptions.find(a => a.key === aggType)?.label}):
                              {numericKeysActive.map(k => {
                                const ag = aggregate(g1.rows, aggType, numericKeysActive);
                                const val = ag[k] ?? ag.COUNT;
                                const label = columns.find(c => c.key === k)?.label || k;
                                return (
                                  <span key={k} className="ml-3">{formatCurrencyBRL(val)} ({label})</span>
                                );
                              })}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              ))}
              {/* Total geral */}
              <div className="border rounded">
                <div className="bg-blue-50 px-3 py-2 text-sm font-semibold">Total Geral</div>
                <div className="p-2 text-xs">
                  {numericKeysActive.map(k => {
                    const label = columns.find(c => c.key === k)?.label || k;
                    const val = Number(grandTotal[k] || 0);
                    return (
                      <span key={k} className="mr-4">{formatCurrencyBRL(val)} ({label})</span>
                    );
                  })}
                  <span>Contagem: {data.length}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Paginação (Padrão Lançamentos) */}
        <div className="px-4 py-3 border-t border-gray-200 bg-white flex flex-col md:flex-row md:items-center md:justify-between space-y-3 md:space-y-0 mt-4">
          <div className="text-sm text-gray-700">
            Exibindo <span className="font-medium">{startIndexUI}</span>–<span className="font-medium">{endIndexUI}</span> de <span className="font-medium">{total || 0}</span> registros
          </div>

          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-700">Registros por página:</label>
            <select
              className="px-2 py-1 border border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none"
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
            >
              {itemsPerPageOptions.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>

          <nav className="inline-flex items-center gap-1" aria-label="Pagination">
            <IconButton
              icon={ChevronsLeft}
              onClick={() => setPage(1)}
              disabled={page === 1}
              variant="secondary"
              size="sm"
              title="Primeira página"
            />
            <IconButton
              icon={ChevronLeft}
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              variant="secondary"
              size="sm"
              title="Página anterior"
            />

            {pageNumbers.map((p, idx) => (
              <button
                key={`${p}-${idx}`}
                onClick={() => typeof p === 'number' && setPage(p)}
                disabled={p === '...'}
                className={`px-3 py-1 border-t border-b border-gray-300 bg-white text-sm transition ${
                  p === page
                    ? 'text-white bg-blue-600 border-blue-600'
                    : 'text-gray-700 hover:bg-gray-50'
                } ${p === '...' ? 'cursor-default' : ''}`}
              >
                {p}
              </button>
            ))}

            <IconButton
              icon={ChevronRight}
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              variant="secondary"
              size="sm"
              title="Próxima página"
            />
            <IconButton
              icon={ChevronsRight}
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages}
              variant="secondary"
              size="sm"
              title="Última página"
            />
          </nav>
        </div>
      </div>
      </>
      )}
    </div>
  );
};

export default Relatorio;